'use strict';

const libPictView = require('pict-view');

/**
 * ManageCustomers — the #/ManageCustomers screen. A platform-super-user-only surface (the start of the bespoke
 * level-5 customer-management tooling): the customers a super-user can administer, each with a "Manage
 * Configuration" action that impersonates the customer's configuration admin so their configuration can be managed.
 *
 * Thin shell (like SuperUserGrant.js) — all data + logic + hand-built HTML live in the ManageCustomers provider,
 * which assigns into the #MC-Body mount. The impersonation itself goes through the single app client method
 * PlatformConfiguration-Application-Web.impersonateCustomerAdmin(); the level gate stays _isSuperUser().
 */
class ManageCustomers extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get provider() { return this.pict.providers.ManageCustomers; }

	onAfterRender(pRenderable)
	{
		this.pict.CSSMap.injectCSS();
		const tmpProvider = this.provider;
		if (tmpProvider)
		{
			tmpProvider.render();
			if (!tmpProvider.state.Loaded && !tmpProvider.state.Loading) { tmpProvider.loadAsync(); }
		}
		return super.onAfterRender(pRenderable);
	}
}

module.exports = ManageCustomers;
module.exports.default_configuration =
{
	ViewIdentifier: 'ManageCustomers',
	DefaultRenderable: 'ManageCustomersWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: /*css*/`
		.mc-wrap { padding: 1.25rem 1.5rem 2rem; }
		.mc-head { margin: 0 0 1rem; }
		.mc-title { font-size:1.5rem; font-weight:700; line-height:1.2; margin:0; color:var(--theme-color-text-primary,#1f2733); }
		.mc-subtitle { font-size:0.9rem; color:var(--theme-color-text-muted,#6b7686); margin:0.2rem 0 0; max-width:60rem; }
		.mc-guard { border:1px solid var(--theme-color-status-warning,#b8860b); border-radius:8px; background:var(--theme-color-background-panel,#fff); padding:1.5rem; text-align:center; color:var(--theme-color-text-secondary,#45505f); }
		.mc-guard-title { font-size:1.05rem; font-weight:700; color:var(--theme-color-text-primary,#1f2733); margin:0 0 0.4rem; }
		.mc-note { padding:0.9rem; font-size:0.9rem; color:var(--theme-color-text-muted,#8894a4); }
		.mc-error { padding:0.9rem; font-size:0.88rem; color:var(--theme-color-status-danger,#c0392b); }
		.mc-filter { width:100%; max-width:32rem; box-sizing:border-box; font-size:0.95rem; padding:0.55rem 0.75rem; margin:0 0 0.8rem;
			border:1px solid var(--theme-color-border-medium,#d5dbe3); border-radius:8px; color:var(--theme-color-text-primary,#1f2733); background:var(--theme-color-background-input,#fff); }
		.mc-filter:focus { outline:none; border-color:var(--theme-color-primary,#2f6fed); box-shadow:0 0 0 2px rgba(47,111,237,0.15); }
		.mc-list { border:1px solid var(--theme-color-border-light,#eef1f5); border-radius:10px; overflow:hidden; max-height:64vh; overflow-y:auto; background:var(--theme-color-background-primary,#fff); max-width:56rem; }
		.mc-head-row { display:flex; align-items:center; gap:0.9rem; padding:0.45rem 0.9rem; font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--theme-color-text-muted,#8a94a3); background:var(--theme-color-background-secondary,#f4f6f9); border-bottom:1px solid var(--theme-color-border-light,#eef1f5); }
		.mc-row { display:flex; align-items:center; gap:0.9rem; padding:0.55rem 0.9rem; border-bottom:1px solid var(--theme-color-border-light,#eef1f5); font-size:0.92rem; color:var(--theme-color-text-primary,#1f2733); }
		.mc-row:last-child { border-bottom:none; }
		.mc-row:hover { background:var(--theme-color-background-secondary,#f4f6f9); }
		.mc-col-name { flex:1 1 auto; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
		.mc-col-code { flex:0 0 8rem; font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:0.82rem; color:var(--theme-color-text-muted,#8a94a3); }
		.mc-col-action { flex:0 0 auto; }
		.mc-disabled { color:var(--theme-color-text-muted,#aab2bf); font-style:italic; font-size:0.82rem; }
		.mc-btn { display:inline-flex; align-items:center; gap:0.4rem; font-size:0.82rem; font-weight:600; cursor:pointer; padding:0.35rem 0.75rem; border-radius:7px;
			border:1px solid var(--theme-color-brand-primary,#156dd1); background:var(--theme-color-brand-primary,#156dd1); color:#fff; white-space:nowrap; }
		.mc-btn:hover { background:var(--theme-color-brand-primary-dark,#115ab0); }
		.mc-btn-caret { font-size:0.7rem; opacity:0.85; }
		/* Each customer is an item (row + optional expanded accounts panel); the item carries the divider. */
		.mc-item { border-bottom:1px solid var(--theme-color-border-light,#eef1f5); }
		.mc-item:last-child { border-bottom:none; }
		.mc-item .mc-row { border-bottom:none; }
		.mc-item.is-expanded { background:var(--theme-color-background-secondary,#f4f6f9); }
		.mc-item.is-expanded > .mc-row:hover { background:transparent; }
		.mc-accounts { display:flex; flex-direction:column; gap:0.3rem; padding:0 0.9rem 0.75rem 1.5rem; }
		.mc-accounts-head { font-size:0.66rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--theme-color-text-muted,#8a94a3); margin:0.1rem 0 0.25rem; }
		.mc-accounts-note { font-size:0.86rem; padding:0.35rem 0; }
		.mc-accounts-note a { color:var(--theme-color-brand-primary,#156dd1); font-weight:600; }
		.mc-account { display:flex; align-items:center; gap:0.6rem; width:100%; max-width:34rem; text-align:left; padding:0.4rem 0.55rem; border:1px solid var(--theme-color-border-light,#e8ebf0); border-radius:8px; background:var(--theme-color-background-primary,#fff); cursor:pointer; }
		.mc-account:hover { border-color:var(--theme-color-brand-primary,#156dd1); background:var(--theme-color-brand-primary-subtle,#eaf2fc); }
		.mc-account-avatar { flex:0 0 auto; width:1.75rem; height:1.75rem; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; background:var(--theme-color-background-secondary,#eef1f6); color:var(--theme-color-text-muted,#8a94a3); font-size:0.8rem; }
		.mc-account-id { flex:1 1 auto; min-width:0; display:flex; flex-direction:column; }
		.mc-account-name { font-size:0.88rem; font-weight:600; color:var(--theme-color-text-primary,#1f2733); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
		.mc-account-login { font-size:0.74rem; color:var(--theme-color-text-muted,#8a94a3); font-family:ui-monospace,SFMono-Regular,Menlo,monospace; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
		.mc-account-go { flex:0 0 auto; font-size:0.78rem; font-weight:600; color:var(--theme-color-brand-primary,#156dd1); display:inline-flex; align-items:center; gap:0.3rem; }
		.mc-btn-remove { border-color:var(--theme-color-status-danger,#c0392b); background:transparent; color:var(--theme-color-status-danger,#c0392b); }
		.mc-btn-remove:hover { background:var(--theme-color-status-danger,#c0392b); color:#fff; }
		.mc-tabs { display:flex; gap:0.25rem; border-bottom:1px solid var(--theme-color-border-light,#eef1f5); margin:0 0 0.9rem; }
		.mc-tab { font-size:0.88rem; font-weight:600; padding:0.5rem 0.95rem; border:none; background:transparent; color:var(--theme-color-text-muted,#6b7686); cursor:pointer; border-bottom:2px solid transparent; margin-bottom:-1px; }
		.mc-tab:hover { color:var(--theme-color-text-primary,#1f2733); }
		.mc-tab.is-active { color:var(--theme-color-primary,#2f6fed); border-bottom-color:var(--theme-color-primary,#2f6fed); }
		.mc-team-help { font-size:0.88rem; color:var(--theme-color-text-muted,#6b7686); margin:0 0 0.8rem; max-width:56rem; }
		.mc-team-help code { font-size:0.85em; background:var(--theme-color-background-tertiary,#eef1f6); padding:0.05rem 0.3rem; border-radius:4px; color:var(--theme-color-text-primary,#1f2733); }
		.mc-team-badge { font-size:0.62rem; font-weight:700; text-transform:uppercase; letter-spacing:0.03em; color:#fff; background:var(--theme-color-status-warning,#b8860b); padding:0.08rem 0.4rem; border-radius:9px; vertical-align:middle; margin-left:0.4rem; }
		.mc-module-picker { display:flex; align-items:center; gap:0.6rem; margin:0 0 0.9rem; }
		.mc-module-picklabel { font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--theme-color-text-muted,#6b7686); }
		.mc-module-custpick { flex:1 1 auto; min-width:22rem; max-width:32rem; }
		/* Plain wrapper around the filter + scrolling list — the single scroll container is the inner .mc-list (#MC-ModuleList);
		   this outer must NOT scroll or a second scrollbar stacks on top of it. */
		.mc-module-results { display:flex; flex-direction:column; gap:0.6rem; max-width:56rem; }
		.mc-app-tag { font-size:0.6rem; font-weight:700; text-transform:uppercase; letter-spacing:0.03em; color:var(--theme-color-brand-primary,#5650e6); background:rgba(86,80,230,0.1); padding:0.08rem 0.4rem; border-radius:9px; vertical-align:middle; margin-left:0.4rem; }
	`,
	Templates:
	[
		{
			Hash: 'ManageCustomers-Wrap',
			Template: /*html*/`
				<div class="mc-wrap">
					<div class="mc-head">
						<h1 class="mc-title">Manage Customers</h1>
						<p class="mc-subtitle">Platform super-users: the customers you can administer. Open <strong>Manage Configuration</strong> on a customer to pick one of its configuration-admin accounts and sign in as them to manage their configuration.</p>
					</div>
					<div id="MC-Body"></div>
				</div>`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'ManageCustomersWrap',
			TemplateHash: 'ManageCustomers-Wrap',
			DestinationAddress: '#PlatformConfiguration-Application-Container',
			RenderMethod: 'replace',
		},
	],
};
